import { storeToRefs } from 'pinia'
import { DialOptions } from '@/legacy/types/entities/phoneNumbers'
import { useFlagStore } from '@/store/flags/flags'
import { amazonConnectStore } from '@/tokens/amazonCCP/store'
import { isRestrictedDialNumber } from './restrictedDialNumbers'
import { useRestrictedDialStore } from './restrictedDialStore'

/**
 * Entry point for placing an outbound call.
 *
 * When the restricted-dial warning is enabled and the target number is on the
 * restricted list, this defers the call: it stashes the attempt and opens a
 * confirmation pop-up instead. Otherwise it dials immediately. The pop-up's
 * "Call anyway" action calls {@link performDial} directly to complete the call.
 * @param options Object with phone number and outbound details
 * @returns void
 */
export async function dial(options: DialOptions) {
  // @ts-ignore
  if (window.Cypress) {
    return
  }

  const { showRestrictedDialWarning } = storeToRefs(useFlagStore())
  if (
    showRestrictedDialWarning.value &&
    isRestrictedDialNumber(options.phoneNumber)
  ) {
    useRestrictedDialStore().requestConfirmation(options)
    return
  }

  await performDial(options)
}

/**
 * Place the outbound call without any restricted-number checks.
 *
 * Routes through Amazon Connect when enabled, otherwise falls back to the Zoom
 * phone link.
 * @param param0 Object with phone number inside
 * @param param0.phoneNumber
 * @param param0.outboundNumber
 * @param param0.outboundQueueArn
 * @returns void
 */
export async function performDial({
  phoneNumber,
  outboundNumber,
  outboundQueueArn,
}: DialOptions) {
  console.debug('Opening call via zoom')

  const { showAmazonCCP } = storeToRefs(useFlagStore())
  if (showAmazonCCP.value) {
    amazonConnectStore().startCall(phoneNumber, outboundQueueArn ?? undefined)
    return
  }

  const outboundNumberStr = outboundNumber
    ? `callerid=+${outboundNumber.countryCode ?? '1'}${
        outboundNumber.phoneNumber
      }`
    : ''

  window.open(`zoomphonecall://${phoneNumber}?${outboundNumberStr}`)
  return
}
